import { default as _Vue } from 'vue';
import { AbstractService } from '@/plugins/podolib/lib/AbstractService';
import { Store } from 'vuex';
import { TEntity } from '@/plugins/podolib/services/entities/Entity';
import i18n, { navigatorLang, normalizeLang } from '@/plugins/i18n';
import { TAcquisition, TPatient } from '@/plugins/podolib/services/entities/EntityPatients';
import { Severity } from '@sentry/types';

export enum Document {
  TOU = 'TOU',
  DPA = 'DPA',
}

export type User = {
  _id: string;
  tenant: string;
  client_id: string;
  connection: string;
  email: string;
  userId: string;
};

export type OwnedOrShared = {
  owned: string[];
  shared: string[];
};

export type Config = {
  lang: string;
  measurementUnit: string;
  sidebar: string;
};

export type Address = {
  street: string;
  zipCode: string;
  city: string;
  state: string;
  country: string;
};

export type Office = {
  _id: string;
  phones: string[];
  registrationDate: string;
  officeName: string;
  address: Address;
};

export type Offices = {
  items: Office[];
  selected: Office['_id'];
};

export type ConsentNewsletter = {
  consented: boolean;
  updatedAt?: string;
  createdAt?: string;
};

export interface ConsentLink {
  lang: string;
  link: string;
}

export type Consent = {
  document: Document | string;
  links: ConsentLink[];
  version: Date;
  consented: boolean;
  ip?: string;
  tacit?: true;
} & ({ tacit: true; date?: Date } | { date: Date });

export type getStarted = {
  completed: boolean;
  updatedAt: string;
  createdAt: string;
};

export type Subscription = {
  name: string;
};

export type Doctor = {
  userId: string;
  firstName: string;
  lastName: string;
  professions: string[];
  patients: OwnedOrShared;
  stations: OwnedOrShared;
  soles: OwnedOrShared;
  config: Config;
  role: string;
  phone?: {
    code: string;
    value: string;
  };
  offices: Offices;
  newsletters?: ConsentNewsletter;
  consents?: Consent[];
  subscriptions?: Subscription[];
  getStarted?: getStarted;
  kitStatus?: 'NO_KIT' | 'KIT_UNCONFIGURED' | 'KIT_CONFIGURED';
  /** @deprecated */
  hasKit?: boolean;
};

export type Extension = {
  lastAcquisitions: TAcquisition[];
  lastPatients: TEntity<TPatient>[];
};

// Service options type
export type Options = {};

// Store state type
export type State = Extension & {
  user: User | null;
  current: Doctor | null;
  loading: boolean;
};

/**
 * @author Damien CORLER <d.corler@zhortech.com>
 * @since 1.0.0
 * @version 1.0.0
 */
export default class DoctorService extends AbstractService<Options, State> {
  /**
   * @param options
   */
  constructor(options: Options) {
    super(options, 'doctor');
  }

  /**
   * Initialize plugin
   */
  async init(Vue: typeof _Vue, store: Store<any>) {
    await super.init(Vue, store);

    const _this = this;

    this.$register(this.defaultState, {
      mutations: {
        SET_USER(state, value) {
          Vue.set(state, 'user', value);
        },
        SET_CURRENT(state, value) {
          if (value !== state.current) {
            _this.Vue.prototype.$pdc.analytics.actions.identify(value);
          }

          Vue.set(state, 'current', value);
        },
        SET_LOADING(state, value: boolean) {
          state.loading = value;
        },
        SET_LAST_ACQUISITIONS(state, lastAcquisitions: State['lastAcquisitions']) {
          state.lastAcquisitions = lastAcquisitions;
        },
        SET_LAST_PATIENTS(state, lastPatients: State['lastPatients']) {
          state.lastPatients = lastPatients;
        },
      },
      getters: {
        _t(state): Doctor | null {
          return state.current;
        },
        lang(state, getters): string {
          if (getters['_t'] && getters['_t'].config) {
            return getters['_t'].config.lang;
          }
          return 'en';
        },
        termsAndConditionsConsented(_, getters): boolean | null {
          if (getters['_t'] && getters['_t'].termsAndConditions) {
            return getters['_t'].termsAndConditions.consented;
          }
          return null;
        },
        newslettersConsented(_, getters): boolean | null {
          if (getters['_t'] && getters['_t'].newsletters) {
            return getters['_t'].newsletters.consented;
          }
          return null;
        },
        selectedOffice(_, getters): Office | undefined {
          if (getters['_t'] && getters['_t'].offices) {
            if (getters['_t'].offices.selected) {
              return getters['_t'].offices.items.find((x: Office) => x._id === getters['_t']!.offices.selected);
            } else {
              return getters['_t'].offices.items[0];
            }
          }
        },
        getOfficeWithId: (_, getters) => (id: string) => {
          if (getters['_t'] && getters['_t'].offices) {
            return getters['_t'].offices.items.find((x: Office) => x._id === id);
          }
        },
      },
    });

    this.store.watch(
      (state) => state.pdc.doctor.current,
      (newValue: TEntity<Doctor>, oldValue: TEntity<Doctor>) => {
        this.logger.log(`🔧 Updating doctor profile`);

        // Set interface lang
        if (newValue && newValue.config.lang) {
          const v = normalizeLang(newValue.config.lang);
          this.setLang(v);
        } else {
          this.setLang(navigatorLang(), true);
        }
      },
    );

    this.Vue.prototype.$pdc.bus.on('app:reset', this.$clean.bind(this));
  }

  startListener() {
    this.Vue.prototype.$pdc.repository.socket.session.on(`doctors patched`, (doctor: TEntity<Doctor>) => {
      this.patchDoctor(doctor);
      this.Vue.prototype.$pdc.bus.emitDebug('doctor:patched', doctor);
    });
  }

  /**
   * Get default state of this store module
   */
  public get defaultState(): State {
    return {
      ...super.defaultState,
      user: null,
      current: null,
      loading: false,
      lastAcquisitions: [],
      lastPatients: [],
    };
  }

  // ---------------------------------------------------------------------------
  // SHORTCUTS

  get user(): User {
    return this.$state('user');
  }

  get current(): TEntity<Doctor> {
    return this.$state('current');
  }

  get loading(): State['loading'] {
    return this.$state('loading');
  }

  get lastAcquisitions(): State['lastAcquisitions'] {
    return this.$state('lastAcquisitions');
  }

  get lastPatients(): State['lastPatients'] {
    return this.$state('lastPatients');
  }

  /**
   * Get user profile
   */
  public async getUser(): Promise<User> {
    try {
      // Logs
      this.logger.log('🚧 Fetching user profile ...');
      // Try to get user
      const userResult = await this.Vue.prototype.$pdc.repository.fetchRessource({
        ressource: 'users',
        params: {
          userId: this.Vue.prototype.$pdc.cognito!.user.attributes.sub,
        },
      });
      // Transform
      const user = userResult.data[0];
      // Store user
      this.$commit('SET_USER', user);
      // Logs
      this.logger.success('✅ User profile fetched');
      // Return user
      return user;
    } catch (e) {
      this.logger.fatal('💥 User profile fetch failed', e);
      // Reset stored ser
      this.$commit('SET_USER', null);
      // Throw error
      throw e;
    }
  }

  /**
   * Get doctor profile
   *
   * @since 1.0.0
   */
  public async getDoctor(): Promise<TEntity<Doctor>> {
    try {
      // Logs
      this.logger.log('🚧 Fetching doctor profile ...');
      // Try to get doctor ...
      const doctorResult = await this.Vue.prototype.$pdc.repository.fetchRessource({
        ressource: 'doctors',
        params: {
          userId: this.user._id,
        },
      });

      // Transform
      const doctor: TEntity<Doctor> = doctorResult.data[0];

      if (!doctor) {
        throw new Error('NotDoctorProfile');
      }

      /**
       * Normalize
       * @see https://zhortech.atlassian.net/browse/POD-204
       */
      if (doctor?.config.lang) {
        doctor.config.lang = normalizeLang(doctor.config.lang);
      }

      // Store doctor
      this.$commit('SET_CURRENT', doctor);
      // Logs
      this.logger.success('✅ Doctor profile fetched');

      /*if (!doctor.offices.items.length || !doctor.offices.selected) {
        throw new Error('NotDoctorProfile');
      }*/

      // Return doctor
      return doctor;
    } catch (e) {
      if (e.message !== 'NotDoctorProfile') {
        this.logger.fatal('💥 Doctor profile fetch failed', e);
        // Reset stored doctor
        this.$commit('SET_CURRENT', null);
      }
      // Throw error
      throw e;
    }
  }

  public patchDoctor(doctor: TEntity<Doctor>): void {
    if (this.user._id && doctor.userId === this.user._id) {
      // If a doctor exist in store...
      try {
        if (this.current) {
          // Check if we need to sync stations
          if (this.current.stations !== doctor.stations) {
            this.Vue.prototype.$pdc.entities!.stations.sync({ mergeWithLastUsedQuery: true });
          }
        }
      } catch (e) {
        this.logger.error(e);
        // Sentry
        this.Vue.prototype.$sentry.captureMessage(e.message, Severity.Critical);
      } finally {
        this.$commit('SET_CURRENT', doctor);
      }
    }
  }

  /**
   * Fetch user and doctor profile
   *
   * Don't change order
   * Own catch
   *
   * @since 1.0.0
   */
  public async fetch(): Promise<void> {
    try {
      // Logs
      this.logger.log('🚧 Fetching profiles ...');
      this.Vue.prototype.$pdc.bus.emitDebug('doctor:fetch-pending');
      await this.getUser();
      await this.getDoctor();
      // Logs
      this.logger.success('✅ Profiles have been fetched');
      this.Vue.prototype.$pdc.bus.emitDebug('doctor:fetch-success');
    } catch (e) {
      if (e.message !== 'NotDoctorProfile') {
        // Logs
        this.logger.fatal('💥 The fetch of profiles have failed', e);
        this.Vue.prototype.$pdc.bus.emitDebug('doctor:fetch-fail', e);
      }

      throw e;
    }
  }

  public setLang(lang: string, fromBrowser = false) {
    i18n.locale = lang;
    this.Vue.prototype.$moment.locale(lang);
    this.logger.info(`🔧 Language is now '${lang}' (from ${fromBrowser ? 'browser' : 'mutation'})`);
  }

  public async getLastAcquisitions() {
    this.$commit('SET_LOADING', true);
    const result = await this.Vue.prototype.$pdc.repository
      .emit('find', 'acquisitions', {
        $limit: 5,
        $populate: 'patient',
        $sort: {
          createdAt: -1,
        },
      })
      .finally(() => {
        this.$commit('SET_LOADING', false);
      });
    this.$commit('SET_LAST_ACQUISITIONS', result.data);
    return result;
  }

  public async getTotalPatientsWithoutDemo() {
    return await this.Vue.prototype.$pdc.repository.emit('find', 'patients', {
      $limit: 0,
      demo: { $exists: false },
    });
  }

  public async getTotalAcquisitionsWithoutDemo() {
    return await this.Vue.prototype.$pdc.repository.emit('find', 'acquisitions', {
      $limit: 0,
      demo: { $exists: false },
    });
  }

  public async getLastPatients() {
    const MONTH = 30 * 24 * 60 * 60 * 1000;
    const result = await this.Vue.prototype.$pdc.repository.emit('find', 'patients', {
      createdAt: {
        $gt: new Date().getTime() - MONTH,
      },
      demo: { $exists: false },
    });
    this.$commit('SET_LAST_PATIENTS', result.data);
    return result;
  }
}
